/**
 * Which sidebar groups the operator has closed.
 *
 * A view preference rather than data — nobody else's sidebar changes when you
 * collapse "Reference" — so it lives in `localStorage` and never reaches the API.
 * If accounts ever become real (there is one shared credential today), this is
 * the seam to move it behind.
 *
 * Kept per *sport*, because the groups are generated from a sport's own
 * definition: closing cricket's Structure group should not close a differently
 * shaped one in swimming.
 *
 * Exposed as a `useSyncExternalStore` source rather than read into state in an
 * effect. `localStorage` *is* an external store, and the effect version both
 * trips the cascading-render rule and quietly ignores the same menu open in
 * another tab. This way a collapse in one tab lands in the other.
 */

const KEY = "omnium.sidebar.v1";

/** Group titles the operator closed. Absent means open, so a group that appears
 *  later — a sport gaining its first venue master — appears open. */
export type Collapsed = readonly string[];

export const NONE: Collapsed = [];

/** Namespaced per sport. */
export function scopeOf(sport: string | undefined): string {
  return sport ?? "_global";
}

export type Stored = Record<string, string[]>;

const NOTHING: Stored = {};

/**
 * Parse whatever is in storage, keeping only what still makes sense.
 *
 * Deliberately forgiving: a browser may hold a shape written by an older build,
 * and the cost of a wrong guess is a group that opens. Throwing would cost the
 * whole sidebar.
 */
export function parse(raw: string | null): Stored {
  if (!raw) return NOTHING;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return NOTHING;
  }
  if (typeof value !== "object" || value === null) return NOTHING;
  const out: Stored = {};
  for (const [scope, closed] of Object.entries(value as Record<string, unknown>)) {
    // Tolerates the shape this replaced, which stored `{ collapsed: [...] }`.
    const list = Array.isArray(closed)
      ? closed
      : typeof closed === "object" && closed !== null
        ? (closed as { collapsed?: unknown }).collapsed
        : undefined;
    if (!Array.isArray(list)) continue;
    out[scope] = list.filter((v): v is string => typeof v === "string");
  }
  return out;
}

// --- the store ------------------------------------------------------------

const listeners = new Set<() => void>();

/**
 * The last parsed value, kept so `getSnapshot` returns a stable reference.
 *
 * `useSyncExternalStore` compares snapshots with `Object.is` and re-renders when
 * they differ, so parsing afresh on every call is an infinite loop rather than a
 * slow path.
 */
let cached: Stored | null = null;

function load(): Stored {
  if (typeof window === "undefined") return NOTHING;
  try {
    return parse(window.localStorage.getItem(KEY));
  } catch {
    // Private mode, or storage full. A sidebar that throws is worse than one
    // that forgets.
    return NOTHING;
  }
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // Another tab writing the same key. Same-tab writes notify directly in `save`,
  // because `storage` deliberately does not fire in the tab that wrote it.
  const onStorage = (event: StorageEvent) => {
    if (event.key !== null && event.key !== KEY) return;
    cached = null;
    listener();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

export function getSnapshot(): Stored {
  cached ??= load();
  return cached;
}

/** Nothing, on the server. The first client paint therefore matches the server's
 *  and React fills the real value in immediately after — the alternative is a
 *  hydration mismatch on every collapsed group. */
export function getServerSnapshot(): Stored {
  return NOTHING;
}

/** Open a closed group, or close an open one. */
export function toggle(scope: string, group: string): void {
  const all = getSnapshot();
  const closed = all[scope] ?? [];
  const next = {
    ...all,
    [scope]: closed.includes(group) ? closed.filter((v) => v !== group) : [...closed, group],
  };
  cached = next;
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      /* the preference is lost, the sidebar still works */
    }
  }
  for (const listener of listeners) listener();
}
